import { useMemo } from 'react';

import { FlowClient, ServiceConnectionClient } from '@@api';

import useApiClient from '../../../common/hooks/useApiClient';
import { FlowRefModel, IntegrationEventRefModel, OperationRefModel, SchemaRefModel } from '../Models/DomainServiceModel';

export class FlowQueries {
	private static DefaultStaleTime = 10 * 60 * 1000;

	constructor(private client: FlowClient, private serviceConnectionClient?: ServiceConnectionClient) {}

	entitySchemasOfContextQueryKey(contextId: string | undefined) {
		return ['flow', 'entities', contextId];
	}

	queryEntitySchemasOfContext(contextId: string | undefined) {
		return {
			queryKey: this.entitySchemasOfContextQueryKey(contextId),
			queryFn: () => this.client.getEntities(contextId!),
			enabled: !!contextId,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	entitySchemasByIdsQueryKey(contextId: string | undefined, ids: string[]) {
		return ['flow', 'entities', contextId, [...ids].sort()];
	}

	queryEntitySchemasByIds(contextId: string | undefined, ids: string[]) {
		const distinctIds = Array.from(new Set(ids)).filter(Boolean);
		return {
			queryKey: this.entitySchemasByIdsQueryKey(contextId, distinctIds),
			queryFn: () => this.client.getEntitiesByIds(contextId!, { ids: distinctIds }),
			enabled: !!contextId && distinctIds.length > 0,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	queryEntitySchemaByRef(ref: SchemaRefModel | undefined, includeNestedTypes: boolean) {
		return {
			queryKey: ['flow', 'entity', ref?.ContextId, ref?.Id, includeNestedTypes],
			queryFn: () => this.client.getEntityById(ref!.ContextId, ref!.Id, includeNestedTypes),
			enabled: !!ref,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	queryIntegrationEventByRef(ref: IntegrationEventRefModel | undefined) {
		return {
			queryKey: ['flow', 'integrationEvent', ref?.Id],
			queryFn: () => this.client.getIntegrationEventById(ref!.Id, true),
			enabled: !!ref,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	queryOperationByRef(ref: OperationRefModel | undefined) {
		return {
			queryKey: ['flow', 'operation', ref?.ContextId, ref?.Id],
			queryFn: () => this.client.getOperationById(ref!.ContextId, ref!.Id, true),
			enabled: !!ref,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	queryFlowById(ref: FlowRefModel | undefined) {
		return {
			queryKey: ['flow', 'byId', ref?.Id],
			queryFn: () => this.client.getById(ref!.Id),
			enabled: !!ref?.Id,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}

	queryServiceConnectionProperty(
		serviceConnectionId: string | undefined,
		propertyName: string | undefined,
		dependentValues: Record<string, any> | undefined,
	) {
		return {
			queryKey: ['service-connection', 'property', serviceConnectionId, propertyName, JSON.stringify(dependentValues ?? {})],
			queryFn: () => this.serviceConnectionClient!.getProperty(
				serviceConnectionId!,
				propertyName!,
				{ dependentValues },
			),
			enabled: !!this.serviceConnectionClient && !!serviceConnectionId && !!propertyName,
			staleTime: FlowQueries.DefaultStaleTime,
		};
	}
}

export default function useFlowQueries() {
	const client = useApiClient(FlowClient);
	const serviceConnectionClient = useApiClient(ServiceConnectionClient);
	return useMemo(() => new FlowQueries(client, serviceConnectionClient), [client, serviceConnectionClient]);
}
